import {
  type Conversation,
  type ConversationState,
  type GetMessagesResponse
} from '@shared/all/domains/messages';
import { postDataSort } from '@shared/all/domains/posts';
import {
  type DetailsReturn,
  EmailVerificationStatus
} from '@shared/all/domains/user';

import {
  getInitialUserFromCookie,
  getInitialUserId
} from '../helpers/parseJwt';

export const localPreferenceActions = {
  initializePreference: 'initializePreference',
  setMuteVideo: 'setMuteVideo',
  updatedLocalPreference: 'updatedLocalPreference',
  SET_THEME: 'SET_THEME'
} as const;

export type LocalPreferenceAction =
  | {
      type: typeof localPreferenceActions.initializePreference;
      payload: Partial<LocalPreferenceState>;
    }
  | { type: typeof localPreferenceActions.setMuteVideo; payload: boolean }
  | {
      type: typeof localPreferenceActions.updatedLocalPreference;
      payload: { updatedSettings: Partial<LocalPreferenceState> };
    }
  | {
      type: typeof localPreferenceActions.SET_THEME;
      payload: 'dark' | 'light' | 'system' | null;
    };

export const navigatorActions = {
  SET_BACK_NAVIGATION: 'SET_BACK_NAVIGATION',
  SET_LOGO_CLICKED: 'SET_LOGO_CLICKED',
  SET_IS_ONLINE: 'SET_IS_ONLINE'
} as const;

export type NavigatorAction =
  | {
      type: typeof navigatorActions.SET_BACK_NAVIGATION;
      payload: BackNavigation | undefined;
    }
  | { type: typeof navigatorActions.SET_LOGO_CLICKED; payload: boolean }
  | { type: typeof navigatorActions.SET_IS_ONLINE; payload: boolean };

export const userActions = {
  SET_IS_AUTHENTICATED: 'SET_IS_AUTHENTICATED',
  SET_AUTH_ERROR: 'SET_AUTH_ERROR',
  SET_USER: 'SET_USER',
  ADD_TRONS: 'ADD_TRONS',
  REMOVE_TRONS: 'REMOVE_TRONS'
} as const;

export type UserAction =
  | { type: typeof userActions.SET_IS_AUTHENTICATED; payload: boolean | null }
  | { type: typeof userActions.SET_AUTH_ERROR; payload: string }
  | { type: typeof userActions.SET_USER; payload: DetailsReturn }
  | { type: typeof userActions.ADD_TRONS; payload: number }
  | { type: typeof userActions.REMOVE_TRONS; payload: number };

export const conversationActions = {
  SET_CONVERSATIONS: 'SET_CONVERSATIONS',
  SET_HAS_NEXT_PAGE: 'SET_HAS_NEXT_PAGE',
  SET_ERROR: 'SET_ERROR',
  SET_LOADING: 'SET_LOADING',
  TOGGLE_CONVERSATION_LIST: 'TOGGLE_CONVERSATION_LIST',
  SET_FETCH_NEXT_PAGE: 'SET_FETCH_NEXT_PAGE',
  UPDATE_CONVERSATION_STATE: 'UPDATE_CONVERSATION_STATE',
  SET_OPEN_CONVERSATIONS: 'SET_OPEN_CONVERSATIONS',
  SET_TYPING: 'SET_TYPING',
  REMOVE_TYPING: 'REMOVE_TYPING',
  RESET_CONVERSATIONS: 'RESET_CONVERSATIONS',
  UPDATE_ACTIVITY_STATUS: 'UPDATE_ACTIVITY_STATUS',
  ADD_CONVERSATIONS: 'ADD_CONVERSATIONS',
  REMOVE_CONVERSATION: 'REMOVE_CONVERSATION',
  UPDATE_CONVERSATION_NAME: 'UPDATE_CONVERSATION_NAME',
  UPDATE_READ_RECEIPT: 'UPDATE_READ_RECEIPT',
  UPDATE_LAST_MESSAGE: 'UPDATE_LAST_MESSAGE',
  REMOVE_PARTICIPANT: 'REMOVE_PARTICIPANT',
  REJECT_DISPUTE: 'REJECT_DISPUTE',
  // Message actions
  SET_MESSAGES: 'SET_MESSAGES',
  ADD_MESSAGES: 'ADD_MESSAGES',
  ADD_NEWER_MESSAGES: 'ADD_NEWER_MESSAGES',
  PREPEND_MESSAGE: 'PREPEND_MESSAGE',
  MARK_MESSAGE_FAILED: 'MARK_MESSAGE_FAILED',
  SET_MESSAGES_LOADING: 'SET_MESSAGES_LOADING',
  SET_MESSAGES_ERROR: 'SET_MESSAGES_ERROR',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  PATCH_MESSAGE: 'PATCH_MESSAGE',
  DELETE_MESSAGE: 'DELETE_MESSAGE'
} as const;

export type ConversationAction =
  | {
      type: typeof conversationActions.SET_CONVERSATIONS;
      payload: Conversation[];
    }
  | { type: typeof conversationActions.SET_HAS_NEXT_PAGE; payload: boolean }
  | { type: typeof conversationActions.SET_ERROR; payload: string | null }
  | { type: typeof conversationActions.SET_LOADING; payload: boolean }
  | {
      type: typeof conversationActions.TOGGLE_CONVERSATION_LIST;
      payload?: undefined;
    }
  | {
      type: typeof conversationActions.SET_FETCH_NEXT_PAGE;
      payload: () => void;
    }
  | {
      type: typeof conversationActions.UPDATE_CONVERSATION_STATE;
      payload: { conversationId: string; state: ConversationState | 'CLOSED' };
    }
  | {
      type: typeof conversationActions.SET_TYPING;
      payload: { conversationId: string; userId: number };
    }
  | {
      type: typeof conversationActions.REMOVE_TYPING;
      payload: { conversationId: string; userId: number };
    }
  | {
      type: typeof conversationActions.RESET_CONVERSATIONS;
      payload?: undefined;
    }
  | {
      type: typeof conversationActions.SET_OPEN_CONVERSATIONS;
      payload: { id: string; conversationState: ConversationState }[];
    }
  | {
      type: typeof conversationActions.UPDATE_ACTIVITY_STATUS;
      payload: {
        userId: number;
        lastActive: Date;
        isActive: boolean;
        conversationId: string;
      };
    }
  | {
      type: typeof conversationActions.ADD_CONVERSATIONS;
      payload: Conversation[];
    }
  | {
      type: typeof conversationActions.REMOVE_CONVERSATION;
      payload: string;
    }
  | {
      type: typeof conversationActions.UPDATE_CONVERSATION_NAME;
      payload: { conversationId: string; name: string };
    }
  | {
      type: typeof conversationActions.UPDATE_READ_RECEIPT;
      payload: string;
    }
  | {
      type: typeof conversationActions.UPDATE_LAST_MESSAGE;
      payload: {
        conversationId: string;
        lastMessage: Conversation['lastMessage'];
      };
    }
  | {
      type: typeof conversationActions.REMOVE_PARTICIPANT;
      payload: { conversationId: string; participantId: number };
    }
  | {
      type: typeof conversationActions.REJECT_DISPUTE;
      payload: string;
    }
  // Message actions
  | {
      type: typeof conversationActions.SET_MESSAGES;
      payload: {
        conversationId: string;
        messages: GetMessagesResponse['messages'];
        hasNextPage: boolean;
        cursor: number | undefined;
        // True when the page is a window around an older message rather than
        // the live end. Omitted means the live end.
        hasNewerPage?: boolean;
      };
    }
  | {
      type: typeof conversationActions.ADD_NEWER_MESSAGES;
      payload: {
        conversationId: string;
        messages: GetMessagesResponse['messages'];
        hasNewerPage: boolean;
        // The newest loaded id the page was requested after. A page that no
        // longer follows the loaded window (it was reset meanwhile) is dropped.
        afterId: number;
      };
    }
  | {
      type: typeof conversationActions.ADD_MESSAGES;
      payload: {
        conversationId: string;
        messages: GetMessagesResponse['messages'];
        hasNextPage: boolean;
        cursor: number | undefined;
      };
    }
  | {
      type: typeof conversationActions.PREPEND_MESSAGE;
      payload: {
        conversationId: string;
        message: GetMessagesResponse['messages'][0] & {
          pending?: boolean;
          failed?: boolean;
          pendingPreviewUris?: Array<{
            uri: string;
            isVideo?: boolean;
            name?: string;
          }>;
        };
      };
    }
  | {
      type: typeof conversationActions.MARK_MESSAGE_FAILED;
      payload: { conversationId: string; messageId: number };
    }
  | {
      type: typeof conversationActions.SET_MESSAGES_LOADING;
      payload: { conversationId: string; isLoading: boolean };
    }
  | {
      type: typeof conversationActions.SET_MESSAGES_ERROR;
      payload: { conversationId: string; error: Error | null };
    }
  | {
      type: typeof conversationActions.CLEAR_MESSAGES;
      payload: string;
    }
  | {
      type: typeof conversationActions.UPDATE_MESSAGE;
      payload: {
        conversationId: string;
        messageId: number;
        content: string;
        edited: boolean;
      };
    }
  | {
      type: typeof conversationActions.DELETE_MESSAGE;
      payload: { conversationId: string; messageId: number };
    }
  | {
      // Generic partial merge onto one message. UPDATE_MESSAGE is specifically
      // an edit (content + edited flag); this carries live side-channel updates
      // like reaction tallies and thread reply counts.
      type: typeof conversationActions.PATCH_MESSAGE;
      payload: {
        conversationId: string;
        messageId: number;
        patch: Partial<LocalMessage>;
      };
    };

export interface SyncState {
  status:
    | 'idle'
    | 'connecting'
    | 'connected'
    | 'syncing'
    | 'completed'
    | 'error'
    | 'disconnected'
    | 'failed'
    | 'decrypt_failed';
  progress: {
    current: number;
    total: number;
    stage: string;
    details: string;
  };
  error: string | null;
  lastSyncTime: number | null;
  syncStats: {
    recordsPulled: number;
    recordsPushed: number;
    conflictsResolved: number;
  };
}

export const initialSyncState: SyncState = {
  status: 'idle',
  progress: {
    current: 0,
    total: 0,
    stage: '',
    details: ''
  },
  error: null,
  lastSyncTime: null,
  syncStats: {
    recordsPulled: 0,
    recordsPushed: 0,
    conflictsResolved: 0
  }
};

export const syncActions = {
  SET_STATUS: 'SET_STATUS',
  SET_PROGRESS: 'SET_PROGRESS',
  SET_ERROR: 'SET_ERROR',
  SET_LAST_SYNC_TIME: 'SET_LAST_SYNC_TIME',
  UPDATE_SYNC_STATS: 'UPDATE_SYNC_STATS',
  RESET_SYNC: 'RESET_SYNC'
} as const;

export type SyncAction =
  | { type: typeof syncActions.SET_STATUS; payload: SyncState['status'] }
  | {
      type: typeof syncActions.SET_PROGRESS;
      payload: Partial<SyncState['progress']>;
    }
  | { type: typeof syncActions.SET_ERROR; payload: string }
  | { type: typeof syncActions.SET_LAST_SYNC_TIME; payload: number }
  | {
      type: typeof syncActions.UPDATE_SYNC_STATS;
      payload: Partial<SyncState['syncStats']>;
    }
  | { type: typeof syncActions.RESET_SYNC; payload?: undefined };

export type DisplayMode = 'data' | 'posts' | 'sortByFactiii' | 'mediaSearch';

export type LocalPreferenceState = {
  postFilterType: 'trending' | 'new' | 'popular' | null;
  postSortBy: postDataSort | null;
  filterBot: boolean;
  // When true, posts by new accounts are hidden from feeds. Off by default;
  // window defined in @shared/all/domains/accountLabels.
  filterNew: boolean;
  // When false (default), board cards (posts with a board position) are hidden
  // from feeds. Toggled on via the "Boards" quick filter to surface them.
  showBoards: boolean;
  displayMode: DisplayMode;
  postModeType: 'home' | 'explore' | 'private' | null;
  muteVideo: boolean;
  theme: 'dark' | 'light' | 'system' | null;
  selectedFactiiis: number[];
  dataFilter: string;
  mediaSearch: string;
};

export type BackNavigation = {
  path: string;
  context?: string;
};

export type NavigatorState = {
  backNavigation?: BackNavigation;
  logoClicked: boolean;
  isOnline: boolean | null;
};

export type UserState = {
  user: DetailsReturn;
};

// Local-state message type adds transient optimistic-UI flags. Server-derived
// messages from getMessages won't set these — only client-side temp inserts.
export type LocalMessage = GetMessagesResponse['messages'][0] & {
  pending?: boolean;
  failed?: boolean;
  pendingPreviewUris?: Array<{ uri: string; isVideo?: boolean; name?: string }>;
};

export interface MessagesState {
  messages: LocalMessage[];
  cursor: number | undefined;
  hasNextPage: boolean;
  // The loaded messages are a window around an older message: newer ones
  // exist on the server that are not loaded. False at the live end.
  hasNewerPage: boolean;
  // Live messages that arrived while the window was detached. They are not
  // inserted — they would land in the middle of history — only counted, for
  // the "Back to now" control.
  heldCount: number;
  isLoading: boolean;
  error: Error | null;
}

export interface ConversationsState {
  conversations: Conversation[];
  openConversations: { id: string; conversationState: ConversationState }[];
  conversationListOpen: boolean;
  hasNextPage: boolean;
  error: string | null;
  fetchNextPage: () => void;
  isLoading: boolean;
  typingTimeouts: Map<string, ReturnType<typeof setTimeout>>;
  // Messages keyed by conversationId
  messages: Record<string, MessagesState>;
}

export const initialConversationsState: ConversationsState = {
  conversations: [],
  conversationListOpen: false,
  hasNextPage: false,
  error: null,
  fetchNextPage: () => {},
  isLoading: false,
  typingTimeouts: new Map<string, ReturnType<typeof setTimeout>>(),
  openConversations: [],
  messages: {}
};

// `cookieUser` may come from `document.cookie` on the client or from
// `next/headers#cookies()` on the server — both paths must produce identical
// initial state or React's hydration check trips.
export const buildInitialUserState = (
  cookieUser: ReturnType<typeof getInitialUserFromCookie>,
  initialUserId = 0
): UserState => ({
  user: cookieUser
    ? {
        ...cookieUser,
        avatarKey: cookieUser.avatarKey || '/images/display-image-default.png'
      }
    : {
        isAuthenticated: null,
        id: initialUserId,
        username: '',
        email: '',
        tag: 'HUMAN',
        emailVerificationStatus: EmailVerificationStatus.UNVERIFIED,
        bio: '',
        avatarKey: '/images/display-image-default.png',
        robohash: '',
        bannerKey: '',
        name: null,
        twoFaEnabled: false,
        hasPassword: false,
        hasPasskey: false,
        onboardingPath: null,
        tronsBalance: 0,
        oauthProviders: [],
        oauthProvider: null,
        isPrivate: false,
        isIncognito: false,
        verificationRequestedAt: null,
        deletionScheduledAt: null
      }
});

// Module-level default — used when no SSR-injected payload is available
// (mobile, tests, or a legacy import path).
export const initialUserState: UserState = buildInitialUserState(
  getInitialUserFromCookie(),
  getInitialUserId()
);

// A truly empty user for state resets (logout, missing token). Never derived
// from cookies — `initialUserState` captures whoever was logged in at module
// load, so spreading it back into context would resurrect their profile.
export const loggedOutUser: UserState['user'] =
  buildInitialUserState(null).user;

export const getDefaultLocalPreference = (): LocalPreferenceState => ({
  postFilterType: 'trending',
  postSortBy: postDataSort.WEEKLY,
  filterBot: false,
  filterNew: false,
  showBoards: false,
  displayMode: 'posts',
  postModeType: 'explore',
  muteVideo: false,
  theme: null,
  selectedFactiiis: [],
  dataFilter: '',
  mediaSearch: ''
});

export const initialLocalPreferenceState: LocalPreferenceState = {
  postFilterType: null,
  postSortBy: null,
  filterBot: false,
  filterNew: false,
  showBoards: false,
  displayMode: 'posts',
  postModeType: 'explore',
  muteVideo: false,
  theme: null,
  selectedFactiiis: [],
  dataFilter: '',
  mediaSearch: ''
};

export const initialNavigatorState: NavigatorState = {
  backNavigation: undefined,
  logoClicked: false,
  // null = loading (waiting for tRPC to confirm backend reachability)
  // true = online (successful tRPC request)
  // false = offline (network disconnected)
  isOnline: null
};

import type { DrizzleDB } from '../database/types';

export interface DatabaseState {
  db: DrizzleDB | null;
  destroy: (() => void) | null;
  disabled: boolean;
  error: string | null;
  showPopup: boolean;
  /** Whether the tab/window is currently visible */
  isTabActive: boolean;
  /** Derived: isTabActive && !!db && !disabled - use this for write guards */
  canWrite: boolean;
  isInitializing: boolean;
  /** Whether hash syncing to server is disabled */
  hashingDisabled: boolean;
  /**
   * True when schema migrations are known but were deferred (e.g. waiting for
   * the vault to unlock so a pre-migration backup can be taken). While true,
   * schema-sensitive queries may fail — the app should show a gate UI instead.
   */
  pendingMigrations: boolean;
}

export const databaseActions = {
  SET_DB: 'SET_DB',
  SET_DISABLED: 'SET_DISABLED',
  SET_ERROR: 'SET_ERROR',
  SET_SHOW_POPUP: 'SET_SHOW_POPUP',
  SET_IS_TAB_ACTIVE: 'SET_IS_TAB_ACTIVE',
  SET_IS_INITIALIZING: 'SET_IS_INITIALIZING',
  SET_HASHING_DISABLED: 'SET_HASHING_DISABLED',
  RESET_STATE: 'RESET_STATE',
  SET_MULTIPLE: 'SET_MULTIPLE'
} as const;

export type DatabaseAction =
  | { type: typeof databaseActions.SET_DB; payload: DrizzleDB | null }
  | { type: typeof databaseActions.SET_DISABLED; payload: boolean }
  | { type: typeof databaseActions.SET_ERROR; payload: string | null }
  | { type: typeof databaseActions.SET_SHOW_POPUP; payload: boolean }
  | { type: typeof databaseActions.SET_IS_TAB_ACTIVE; payload: boolean }
  | { type: typeof databaseActions.SET_IS_INITIALIZING; payload: boolean }
  | { type: typeof databaseActions.SET_HASHING_DISABLED; payload: boolean }
  | { type: typeof databaseActions.RESET_STATE; payload?: undefined }
  | {
      type: typeof databaseActions.SET_MULTIPLE;
      payload: Partial<Omit<DatabaseState, 'canWrite'>>;
    };

export const initialDatabaseState: DatabaseState = {
  db: null,
  destroy: null,
  disabled: false,
  error: null,
  showPopup: false,
  isTabActive: true,
  canWrite: false,
  isInitializing: false,
  hashingDisabled: true,
  pendingMigrations: false
};

// Vault types (mobile-only feature)
export type VaultBackupOperation =
  | 'upload'
  | 'download'
  | 'encrypt'
  | 'decrypt'
  | 'zip'
  | 'unzip'
  | null;

export interface VaultBackupProgress {
  isActive: boolean;
  operation: VaultBackupOperation;
  status: string;
  progress: number; // 0-1
  processedBytes: number;
  totalBytes: number;
  error: string | null;
}

export interface VaultState {
  isSetup: boolean;
  isLocked: boolean;
  loading: boolean;
  masterKey: string | null;
  vault: unknown; // VaultInstance on mobile
  backupProgress: VaultBackupProgress;
}

export const initialBackupProgress: VaultBackupProgress = {
  isActive: false,
  operation: null,
  status: '',
  progress: 0,
  processedBytes: 0,
  totalBytes: 0,
  error: null
};

export const vaultActions = {
  SET_IS_SETUP: 'SET_IS_SETUP',
  SET_IS_LOCKED: 'SET_IS_LOCKED',
  SET_LOADING: 'SET_LOADING',
  SET_MASTER_KEY: 'SET_MASTER_KEY',
  SET_VAULT: 'SET_VAULT',
  SET_MULTIPLE: 'SET_MULTIPLE',
  RESET_STATE: 'RESET_STATE',
  // Backup progress actions
  START_BACKUP: 'START_BACKUP',
  UPDATE_BACKUP_PROGRESS: 'UPDATE_BACKUP_PROGRESS',
  SET_BACKUP_STATUS: 'SET_BACKUP_STATUS',
  BACKUP_ERROR: 'BACKUP_ERROR',
  FINISH_BACKUP: 'FINISH_BACKUP'
} as const;

export type VaultAction =
  | { type: typeof vaultActions.SET_IS_SETUP; payload: boolean }
  | { type: typeof vaultActions.SET_IS_LOCKED; payload: boolean }
  | { type: typeof vaultActions.SET_LOADING; payload: boolean }
  | { type: typeof vaultActions.SET_MASTER_KEY; payload: string | null }
  | { type: typeof vaultActions.SET_VAULT; payload: unknown }
  | { type: typeof vaultActions.SET_MULTIPLE; payload: Partial<VaultState> }
  | { type: typeof vaultActions.RESET_STATE }
  // Backup progress actions
  | {
      type: typeof vaultActions.START_BACKUP;
      payload: {
        operation: VaultBackupOperation;
        status: string;
        totalBytes?: number;
      };
    }
  | {
      type: typeof vaultActions.UPDATE_BACKUP_PROGRESS;
      payload: {
        progress: number;
        processedBytes: number;
        totalBytes?: number;
      };
    }
  | {
      type: typeof vaultActions.SET_BACKUP_STATUS;
      payload: { operation?: VaultBackupOperation; status: string };
    }
  | { type: typeof vaultActions.BACKUP_ERROR; payload: string }
  | { type: typeof vaultActions.FINISH_BACKUP };

export const initialVaultState: VaultState = {
  isSetup: false,
  isLocked: true,
  loading: true,
  masterKey: null,
  vault: null,
  backupProgress: initialBackupProgress
};

export interface DatabaseBackupInfo {
  id: string;
  timestamp: number;
  size: number;
}
